import { useEffect, useState } from "react";
import { authApi } from "../../services/authApi";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { DataTable } from "../../components/common/DataTable";
import { ConfirmDialog } from "../../components/common/ConfirmDialog";

export function DevicesTab() {
  const { deviceFingerprint, deviceName } = useAuth();
  const { notify } = useToast();
  const [devices, setDevices] = useState(null);
  const [pendingRevoke, setPendingRevoke] = useState(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    const r = await authApi.trustedDevices();
    setDevices(r.trusted_devices);
  }

  useEffect(() => { load(); }, []);

  const isCurrentTrusted = devices?.some((d) => d.device_fingerprint === deviceFingerprint);

  async function trustCurrentDevice() {
    setBusy(true);
    try {
      await authApi.trustDevice(deviceName, deviceFingerprint, 90);
      await load();
      notify("This device is now trusted for 90 days", "success");
    } catch (err) {
      notify(err.apiMessage || "Could not trust device", "danger");
    } finally {
      setBusy(false);
    }
  }

  async function confirmRevoke() {
    setBusy(true);
    try {
      await authApi.revokeDevice(pendingRevoke.device_id);
      await load();
      notify("Device revoked", "success");
    } catch (err) {
      notify(err.apiMessage || "Could not revoke device", "danger");
    } finally {
      setBusy(false);
      setPendingRevoke(null);
    }
  }

  return (
    <Card
      title="Trusted Devices"
      actions={!isCurrentTrusted && <Button size="sm" onClick={trustCurrentDevice} disabled={busy}>Trust This Device (90 days)</Button>}
    >
      <DataTable
        rows={devices}
        loading={!devices}
        rowKey="device_id"
        emptyTitle="No trusted devices"
        emptyDescription="Trusting a device skips extra MFA step-ups on it for 90 days."
        columns={[
          { key: "device_name", header: "Device" },
          { key: "trust_date", header: "Trusted Since", render: (r) => new Date(r.trust_date).toLocaleDateString() },
          { key: "expiry_date", header: "Expires", render: (r) => new Date(r.expiry_date).toLocaleDateString() },
          { key: "last_used", header: "Last Used", render: (r) => (r.last_used ? new Date(r.last_used).toLocaleString() : "—") },
          {
            key: "actions",
            header: "",
            render: (r) => <Button size="sm" variant="danger" onClick={() => setPendingRevoke(r)}>Revoke</Button>,
          },
        ]}
      />
      <ConfirmDialog
        open={!!pendingRevoke}
        onClose={() => setPendingRevoke(null)}
        onConfirm={confirmRevoke}
        title="Revoke trusted device?"
        confirmLabel="Revoke"
        danger
        busy={busy}
      >
        <p>{pendingRevoke?.device_name} will need to complete MFA again on its next sign-in.</p>
      </ConfirmDialog>
    </Card>
  );
}
